import React from 'react';
import { useNavigate } from 'react-router-dom';
import './SolarMonitorDashboard.css';
import { getUser, logout } from '../services/authService';
import { formatDisplayDateTime } from '../utils/dateTimeFormat';

const SolarMonitorDashboard = ({ data }) => {
  const navigate = useNavigate();
  const user = getUser();
  // Default values if data is not available
  const defaultData = {
    kseb: { phaseR: 0, phaseY: 0, phaseB: 0 },
    inverter: { phaseR: 0, phaseY: 0, phaseB: 0 },
    current: { c1: 0, c2: 0, c3: 0 },
    temperature1: 0,
    temperature2: 0,
    batteryVoltage: 0,
    panelVoltage1: 0,
    panelVoltage2: 0,
    panelVoltage3: 0,
    panelVoltage4: 0,
    panelVoltage5: 0,
    panelVoltage6: 0,
    panelVoltage7: 0,
    panelVoltage8: 0,
    panelVoltage9: 0,
    panelVoltage10: 0,
    panelVoltage11: 0,
    panelVoltage12: 0,
    panelVoltage13: 0,
    panelVoltage14: 0,
    panelVoltage15: 0,
    panelVoltage16: 0,
    panelVoltage17: 0,
    panelVoltage18: 0,
    loadOn: ''
  };

  // Merge API data with defaults to ensure all fields are available
  const displayData = {
    ...defaultData,
    ...data,
    kseb: data?.kseb || defaultData.kseb,
    inverter: data?.inverter || defaultData.inverter,
    current: data?.current || defaultData.current,
    temperature1: data?.temperature1 ?? defaultData.temperature1,
    temperature2: data?.temperature2 ?? defaultData.temperature2,
    batteryVoltage: data?.batteryVoltage ?? defaultData.batteryVoltage,
    panelVoltage1: data?.panelVoltage1 ?? defaultData.panelVoltage1,
    panelVoltage2: data?.panelVoltage2 ?? defaultData.panelVoltage2,
    panelVoltage3: data?.panelVoltage3 ?? defaultData.panelVoltage3,
    panelVoltage4: data?.panelVoltage4 ?? defaultData.panelVoltage4,
    panelVoltage5: data?.panelVoltage5 ?? defaultData.panelVoltage5,
    panelVoltage6: data?.panelVoltage6 ?? defaultData.panelVoltage6,
    panelVoltage7: data?.panelVoltage7 ?? defaultData.panelVoltage7,
    panelVoltage8: data?.panelVoltage8 ?? defaultData.panelVoltage8,
    panelVoltage9: data?.panelVoltage9 ?? defaultData.panelVoltage9,
    panelVoltage10: data?.panelVoltage10 ?? defaultData.panelVoltage10,
    panelVoltage11: data?.panelVoltage11 ?? defaultData.panelVoltage11,
    panelVoltage12: data?.panelVoltage12 ?? defaultData.panelVoltage12,
    panelVoltage13: data?.panelVoltage13 ?? defaultData.panelVoltage13,
    panelVoltage14: data?.panelVoltage14 ?? defaultData.panelVoltage14,
    panelVoltage15: data?.panelVoltage15 ?? defaultData.panelVoltage15,
    panelVoltage16: data?.panelVoltage16 ?? defaultData.panelVoltage16,
    panelVoltage17: data?.panelVoltage17 ?? defaultData.panelVoltage17,
    panelVoltage18: data?.panelVoltage18 ?? defaultData.panelVoltage18,
    time: data?.time,
    loadOn: data?.loadOn != null && data?.loadOn !== undefined ? String(data.loadOn) : defaultData.loadOn
  };

  const loadOnNorm = String(displayData.loadOn || '').trim().toLowerCase();
  const loadKsebActive = loadOnNorm === 'kseb';
  const loadInverterActive = loadOnNorm === 'inverter';

  // Format number: no decimals by default; use 1 decimal for temperature1/temperature2 only
  const formatValue = (value, decimals = 0) => {
    if (value === null || value === undefined) return '0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toFixed(decimals);
  };

  const formatVoltageReading = (value) => {
    if (value === null || value === undefined) return '0 V';
    if (typeof value === 'number') {
      if (isNaN(value)) return '0 V';
      return `${value.toFixed(0)} V`;
    }
    const s = String(value).trim();
    if (!s) return '0 V';
    if (/v\s*$/i.test(s)) return s;
    const num = parseFloat(s.replace(/,/g, '.'));
    if (!isNaN(num)) return `${s} V`;
    return s;
  };

  // Check if value is negative for styling
  const isNegative = (value) => {
    if (value === null || value === undefined) return false;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return !isNaN(num) && num < 0;
  };

  const formatLastUpdated = (timeValue) => formatDisplayDateTime(timeValue);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const organisationName = (user?.organisationName || '').trim() || '—';

  return (
    <div className="solar-monitor-container">
      <header className="dashboard-header">
        <div className="dashboard-branding">
          <div className="dashboard-branding-inner">
            <img
              src="/logo.png"
              alt="Logo"
              className="dashboard-brand-logo"
              onError={(e) => {
                if (e.target.src.endsWith('/logo.png')) {
                  e.target.onerror = null;
                }
              }}
            />
            <p className="dashboard-org-name">{organisationName}</p>
          </div>
        </div>
        <div className="dashboard-header-bar">
          <h2 className="solar-monitor-title">SOLAR PCU MONITOR</h2>
          <div className="last-updated">
            <span className="last-update">Last Updated: {formatLastUpdated(displayData.time) || '—'}</span>
          </div>
          <div className="dashboard-header-actions">
            <button type="button" className="dashboard-settings-btn" onClick={() => navigate('/settings')}>
              Settings
            </button>
            <button type="button" className="dashboard-logout-btn" onClick={handleLogout}>Logout</button>
          </div>
        </div>
      </header>

      <div className="monitor-content">
        {/* VOLTAGE Section - Card */}
        <div className="dashboard-card voltage-section">
          <div className="voltage-section-header">
            <div className="load-on-row" role="group" aria-label="Load on">
              <span className="load-on-label">LOAD ON:</span>
              <div className="load-on-buttons">
                <button
                  type="button"
                  className={`load-on-btn${loadKsebActive ? ' active' : ''}`}
                  disabled
                  aria-pressed={loadKsebActive}
                >
                  KSEB
                </button>
                <button
                  type="button"
                  className={`load-on-btn${loadInverterActive ? ' active' : ''}`}
                  disabled
                  aria-pressed={loadInverterActive}
                >
                  Inverter
                </button>
              </div>
            </div>
            <div className="voltage-section-label">VOLTAGE</div>
            <div className="voltage-header-spacer" aria-hidden="true" />
          </div>
          <div className="voltage-sources-row">
            <div className="voltage-source">
              <div className="voltage-source-label">KSEB</div>
              <div className="phases-container">
                <div className="phase-group">
                  <label className="phase-label">PHASE R</label>
                  <input 
                    type="text" 
                    className={`phase-input ${isNegative(displayData.kseb?.phaseR) ? 'negative-value' : ''}`}
                    value={`${formatValue(displayData.kseb?.phaseR)} V`}
                    readOnly
                  />
                </div>
                <div className="phase-group">
                  <label className="phase-label">PHASE Y</label>
                  <input 
                    type="text" 
                    className={`phase-input ${isNegative(displayData.kseb?.phaseY) ? 'negative-value' : ''}`}
                    value={`${formatValue(displayData.kseb?.phaseY)} V`}
                    readOnly
                  />
                </div>
                <div className="phase-group">
                  <label className="phase-label">PHASE B</label>
                  <input 
                    type="text" 
                    className={`phase-input ${isNegative(displayData.kseb?.phaseB) ? 'negative-value' : ''}`}
                    value={`${formatValue(displayData.kseb?.phaseB)} V`}
                    readOnly
                  />
                </div>
              </div>
            </div>
            <div className="voltage-source">
              <div className="voltage-source-label">INVERTER</div>
              <div className="phases-container">
                <div className="phase-group">
                  <label className="phase-label">PHASE R</label>
                  <input 
                    type="text" 
                    className={`phase-input ${isNegative(displayData.inverter?.phaseR) ? 'negative-value' : ''}`}
                    value={`${formatValue(displayData.inverter?.phaseR)} V`}
                    readOnly
                  />
                </div>
                <div className="phase-group">
                  <label className="phase-label">PHASE Y</label>
                  <input 
                    type="text" 
                    className={`phase-input ${isNegative(displayData.inverter?.phaseY) ? 'negative-value' : ''}`}
                    value={`${formatValue(displayData.inverter?.phaseY)} V`}
                    readOnly
                  />
                </div>
                <div className="phase-group">
                  <label className="phase-label">PHASE B</label>
                  <input 
                    type="text" 
                    className={`phase-input ${isNegative(displayData.inverter?.phaseB) ? 'negative-value' : ''}`}
                    value={`${formatValue(displayData.inverter?.phaseB)} V`}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CURRENT Section - Card */}
        <div className="dashboard-card current-section">
          <div className="current-section-label">CURRENT</div>
          <div className="phases-container">
            <div className="phase-group">
              <label className="current-label">C1</label>
              <input 
                type="text" 
                className={`phase-input ${isNegative(displayData.current?.c1) ? 'negative-value' : ''}`}
                value={`${formatValue(displayData.current?.c1)} A`}
                readOnly
              />
            </div>
            <div className="phase-group">
              <label className="current-label">C2</label>
              <input 
                type="text" 
                className={`phase-input ${isNegative(displayData.current?.c2) ? 'negative-value' : ''}`}
                value={`${formatValue(displayData.current?.c2)} A`}
                readOnly
              />
            </div>
            <div className="phase-group">
              <label className="current-label">C3</label>
              <input 
                type="text" 
                className={`phase-input ${isNegative(displayData.current?.c3) ? 'negative-value' : ''}`}
                value={`${formatValue(displayData.current?.c3)} A`}
                readOnly
              />
            </div>
          </div>
        </div>

        {/* Temperature & Battery Voltage - Card */}
        <div className="dashboard-card bottom-row">
          <div className="bottom-row-inner">
          <div className="temperature-group">
            <label className="parameter-label">TEMPERATURE 1</label>
            <input 
              type="text" 
              className={`temperature-input ${isNegative(displayData.temperature1) ? 'negative-value' : ''}`}
              value={`${formatValue(displayData.temperature1, 1)} °C`}
              readOnly
            />
          </div>
          <div className="temperature-group">
            <label className="parameter-label">TEMPERATURE 2</label>
            <input 
              type="text" 
              className={`temperature-input ${isNegative(displayData.temperature2) ? 'negative-value' : ''}`}
              value={`${formatValue(displayData.temperature2, 1)} °C`}
              readOnly
            />
          </div>
          <div className="voltage-group">
            <label className="parameter-label">BATTERY VOLTAGE</label>
            <input 
              type="text" 
              className={`voltage-input ${isNegative(displayData.batteryVoltage) ? 'negative-value' : ''}`}
              value={formatVoltageReading(displayData.batteryVoltage)}
              readOnly
            />
          </div>
          </div>
        </div>
      </div>

      <footer className="dashboard-footer">
        <div className="footer-datetime">{formatLastUpdated(displayData.time) || '—'}</div>
        <div className="footer-buttons">
          <button 
            className="footer-btn footer-btn-panel-voltage"
            onClick={() => navigate('/panel-voltage')}
          >
            PANEL VOLTAGE
          </button>
          <button 
            className="footer-btn footer-btn-report"
            onClick={() => navigate('/report')}
          >
            REPORT
          </button>
        </div>
      </footer>
    </div>
  );
};

export default SolarMonitorDashboard;

